import { Box } from '@mui/material';
import { useIntl } from 'react-intl';
import EditableWindowLevelValue, {
    WindowLevelField,
} from '~/viewportGrid/tile/EditableWindowLevelValue';
import SuvUnitTooltip from '~/components/Tooltips/SuvUnitTooltip';
import { IntensityScale } from '~/domain/dicom/intensityScale';
import { enqueueSnackbar } from '~/utils/notifications';
import { WindowLevelDelta, WindowLevelSource } from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { consoleGroupSx, READOUT_DIM } from '../chrome';
import { NO_VALUE } from './tileViewModel';
import { WindowLevel } from '~/domain/patient/windowLevel';

interface WindowLevelValuesProps {
    /** Per-series window/level truth, which also supplies the accepted range and the scale. */
    readonly source: WindowLevelSource;
    readonly onSetWindowLevel: (windowLevel: WindowLevel) => void;
    readonly onNudgeWindowLevel: (delta: WindowLevelDelta) => void;
}

/** The bar supplies the readout's type; only the column width is ours. */
const VALUE_SX = { minWidth: 26 };

const LABEL_SX = { color: READOUT_DIM };

/**
 * The window width and center labels with no value: the place the values of
 * a series take once they exist. Not editable.
 */
export function EmptyWindowLevelValues() {
    return (
        <>
            {['WW', 'WC'].map((label) => (
                <Box key={label} sx={consoleGroupSx(0.5)}>
                    <Box component="span" sx={LABEL_SX}>
                        {label}:
                    </Box>
                    <Box component="span" sx={{ ...VALUE_SX, ...LABEL_SX }}>
                        {NO_VALUE}
                    </Box>
                </Box>
            ))}
        </>
    );
}

/**
 * The stored step one drag increment moves a value by. The increment is a
 * count of the scale's own steps, so a PET read to a tenth of an SUV moves
 * by tenths of an SUV, not by stored units too small to see. A series
 * draws whole stored units, so a step finer than one is widened to one.
 */
function stepOf(
    field: WindowLevelField,
    increments: number,
    scale: IntensityScale
): WindowLevelDelta {
    const stored = Math.round(
        scale.toStored(increments * 10 ** -scale.decimals)
    );
    const delta = stored === 0 ? Math.sign(increments) : stored;
    return field === 'window'
        ? { windowWidth: delta, windowCenter: 0 }
        : { windowWidth: 0, windowCenter: delta };
}

/**
 * The window width and center of one series, editable where they are read.
 *
 * Both values stay on the bar: dragging one nudges it and clicking it opens
 * it for typing, so changing a window never costs a trip through a menu.
 * Both are read and typed on the scale the series' readout uses, with the
 * unit named in each label, so a PET window is typed in SUV or activity
 * units and lands on the stored intensity it names; hovering a PET unit
 * shows the acquisition record its SUV is computed from. A typed value the
 * series does not accept reverts, a typed width below the least one the
 * field accepts also says so, and nothing is clamped out of sight.
 */
export default function WindowLevelValues({
    source,
    onSetWindowLevel,
    onNudgeWindowLevel,
}: WindowLevelValuesProps) {
    const intl = useIntl();
    useChangeVersion(source);
    const windowLevel = source.getWindowLevel();
    const bounds = source.getLevelRange();
    const readout = source.getReadout();
    if (!windowLevel || !bounds || !readout) {
        return <EmptyWindowLevelValues />;
    }
    const petMetadata = source.getPetMetadata();
    const unit = readout.unit ? (
        <>
            (
            <SuvUnitTooltip metadata={petMetadata}>
                {readout.unit}
            </SuvUnitTooltip>
            )
        </>
    ) : null;

    const write = (field: WindowLevelField, value: number): void =>
        onSetWindowLevel(
            field === 'window'
                ? new WindowLevel(value, windowLevel.level)
                : new WindowLevel(windowLevel.window, value)
        );

    const fields: ReadonlyArray<{
        readonly field: WindowLevelField;
        readonly label: string;
        readonly value: number;
    }> = [
        { field: 'window', label: 'WW', value: windowLevel.window },
        { field: 'center', label: 'WC', value: windowLevel.level },
    ];

    return (
        <>
            {fields.map(({ field, label, value }) => (
                <Box key={field} sx={consoleGroupSx(0.5)}>
                    <Box component="span" sx={LABEL_SX}>
                        {label}
                        {unit}:
                    </Box>
                    <EditableWindowLevelValue
                        value={value}
                        field={field}
                        bounds={bounds}
                        scale={readout.scale}
                        valueUnit={readout.valueUnit}
                        onNudge={(increments) =>
                            onNudgeWindowLevel(
                                stepOf(field, increments, readout.scale)
                            )
                        }
                        onCommit={(next) => write(field, next)}
                        onWidthBelowMinimum={(minimum) =>
                            enqueueSnackbar(
                                intl.formatMessage(
                                    { id: 'windowLevel.widthBelowMinimum' },
                                    { minimum }
                                ),
                                { variant: 'warning' }
                            )
                        }
                        sx={VALUE_SX}
                        testId={`window-${field}`}
                    />
                </Box>
            ))}
        </>
    );
}
